import NextNProgress from 'nextjs-progressbar'
import Head from 'next/head'
import React from 'react'
import { AppProps } from 'next/app'
import { CacheProvider, EmotionCache } from '@emotion/react'
import { ToastContainer } from 'react-toastify'
import NiceModal from '@ebay/nice-modal-react'
import Script from 'next/script'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { MuiThemeProvider } from '../themes'
import createEmotionCache from '../createEmotionCache'
import Web3Context from '@/web3/context'
import 'react-toastify/dist/ReactToastify.css'
import { WalletProvider } from '@/components/common/WalletContext'
import { BtcProvider } from '@/context/BtcContext/context'

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache
}

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache()

function App(props: MyAppProps) {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props

  return (
    <>
      <Head>
        <title>MultiBit</title>
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/favicon.ico" />
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <meta
          name="description"
          content="MultiBit is a cross-chain bridge dedicated to the ecological interoperability between the BTC chain and other chains."
        />
        <meta name="keywords" content="MultiBit,MultiBit Bridge,BRC20 Bridge" />

        <meta property="twitter:card" content="summary" />
        <meta property="twitter:site" content="https://app.multibit.exchange/" />
        <meta property="twitter:creator" content="@MultiBit" />
        <meta property="twitter:image" content="https://app.multibit.exchange/imgs/multibit_1d591c.jpg" />
        <meta property="twitter:url" content="https://app.multibit.exchange/" />
        <meta property="twitter:title" content="MultiBit" />
        <meta
          property="twitter:description"
          content="MultiBit is a cross-chain bridge dedicated to the ecological interoperability between the BTC chain and other chains."
        />

        <meta property="og:title" content="MultiBit" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.multibit.exchange/" />
        <meta property="og:image" content="https://app.multibit.exchange/imgs/multibit_1d591c.jpg" />
        <meta
          property="og:description"
          content="MultiBit is a cross-chain bridge dedicated to the ecological interoperability between the BTC chain and other chains."
        />
      </Head>

      <CacheProvider value={emotionCache}>
        <Web3Context>
          <MuiThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <NextNProgress color="primary" height={2} />
              <ToastContainer />
              <BtcProvider>
                <WalletProvider>
                  <NiceModal.Provider>
                    <Component {...pageProps} />
                  </NiceModal.Provider>
                </WalletProvider>
              </BtcProvider>
            </LocalizationProvider>
          </MuiThemeProvider>
        </Web3Context>
      </CacheProvider>

      <Script src="https://www.google.com/recaptcha/api.js?render=6Lf2RR8mAAAAAFLVWJwJ-pDIWKHjX1q1q51oJnii" />

      {/* Google analytics */}
      <Script async src="https://www.googletagmanager.com/gtag/js?id=G-ZQRLGCEHWS" />
      <Script
        id="googleAnalytics"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-ZQRLGCEHWS');
            `,
        }}
      />
    </>
  )
}

export default App
